import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { Dialog, DialogTitle, DialogDescription } from '../components_catalyst/dialog';
import { Button } from '../components_catalyst/button';
import { Input } from '../components_catalyst/input';
import { SaveAndRefresh } from '../components_misc/SaveAndRefresh';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { SignUpLink1 } from '../types';
import { loadSignupLinks, createSignupLink } from '../store/admin1Slice';
import { sendSignUpLink } from '../utils/sendSignUpLink';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

interface CopyButtonState {
  id: string | null;
  copied: boolean;
}

export function AdminSignupLinksPage() {
  const dispatch = useDispatch<AppDispatch>();
  const signupLinks = useSelector((state: RootState) => state.admin1.signupLinks);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<SignUpLink1 | null>(null);
  const [expandedEmails, setExpandedEmails] = useState<{ [key: string]: boolean }>({});
  const [newLinkData, setNewLinkData] = useState({
    toEmail: '',
    accountType: 'alpha_guest' as 'alpha_guest' | 'alpha_member',
    expirationDays: 7,
  });
  const [copyState, setCopyState] = useState<CopyButtonState>({ id: null, copied: false });
  const [sendingStates, setSendingStates] = useState<{ [key: string]: boolean }>({});
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);

  useEffect(() => {
    dispatch(loadSignupLinks());
  }, [dispatch]);

  const handleRefresh = useCallback(async () => {
    await dispatch(loadSignupLinks());
  }, [dispatch]);

  const handleEdit = (link: SignUpLink1) => {
    setSelectedLink(link);
    setIsEditDialogOpen(true);
  };

  const handleGenerateLink = async () => {
    try {
      await dispatch(createSignupLink(newLinkData));
      setIsGenerateDialogOpen(false);
      setNewLinkData({
        toEmail: '',
        accountType: 'alpha_guest',
        expirationDays: 7,
      });
      handleRefresh();
    } catch (error) {
      console.error('Error generating signup link:', error);
    }
  };

  const toggleEmailExpand = (emailIndex: string) => {
    setExpandedEmails((prev) => ({
      ...prev,
      [emailIndex]: !prev[emailIndex],
    }));
  };

  const handleCopy = async (url: string, id: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyState({ id, copied: true });
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopyState({ id: null, copied: false });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSendLink = async (link: SignUpLink1) => {
    try {
      setSendingStates((prev) => ({ ...prev, [link.signUpCode]: true }));

      await sendSignUpLink({
        signUpLink: link,
        /*
        fromName: '30to80 Team',
        customMessage: `
          You've been invited to join 30to80!
          
          Click this link to create your account: ${link.url}
          
          Looking forward to collaborating with you!
        `,
        */
      });

      // Show success message
      setSendSuccess(link.signUpCode);
      setTimeout(() => setSendSuccess(null), 2000);

      // Refresh the list to show updated status
      handleRefresh();
    } catch (error) {
      console.error('Error sending signup link:', error);
      // You might want to show an error toast/message here
    } finally {
      setSendingStates((prev) => ({ ...prev, [link.signUpCode]: false }));
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Signup Links</h1>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-4">
          <Button onClick={() => setIsGenerateDialogOpen(true)}>Generate Signup Link</Button>
          <SaveAndRefresh onRefresh={handleRefresh} />
        </div>
      </div>

      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle">
            <table className="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                    Actions
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">Email</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                    Account Type
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                    Expires
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                    Attempts
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                    Emails
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {signupLinks
                  ?.slice()
                  .sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime())
                  .map((link) => (
                    <tr key={link.signUpCode}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Button onClick={() => handleEdit(link)} className="px-2 py-1 text-sm">
                            Edit
                          </Button>
                          <Button
                            onClick={() => handleCopy(link.url, link.signUpCode)}
                            className="relative px-2 py-1 text-sm"
                            title="Copy signup link"
                          >
                            {copyState.id === link.signUpCode && copyState.copied ? (
                              <CheckIcon className="h-4 w-4 text-green-500" />
                            ) : (
                              <ClipboardDocumentIcon className="h-4 w-4" />
                            )}
                            {copyState.id === link.signUpCode && copyState.copied && (
                              <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                                Copied!
                              </span>
                            )}
                          </Button>
                          <Button
                            onClick={() => handleSendLink(link)}
                            className="relative px-2 py-1 text-sm"
                            title="Send signup link email"
                            disabled={sendingStates[link.signUpCode]}
                          >
                            {sendingStates[link.signUpCode] ? (
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                            ) : (
                              <PaperAirplaneIcon className="h-4 w-4" />
                            )}
                            {sendSuccess === link.signUpCode && (
                              <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                                Sent!
                              </span>
                            )}
                          </Button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{link.toEmail}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{link.status}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{link.accountType}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {new Date(link.createdOn).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {new Date(link.expirationDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{link.attempts}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{link.emails?.length || 0}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Generate Link Dialog */}
      <Dialog open={isGenerateDialogOpen} onClose={() => setIsGenerateDialogOpen(false)}>
        <DialogTitle>Generate Signup Link</DialogTitle>
        <DialogDescription>Create a new signup link for a user.</DialogDescription>

        <div className="mt-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <Input
              value={newLinkData.toEmail}
              onChange={(e) => setNewLinkData((prev) => ({ ...prev, toEmail: e.target.value }))}
              type="email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Account Type</label>
            <select
              value={newLinkData.accountType}
              onChange={(e) =>
                setNewLinkData((prev) => ({
                  ...prev,
                  accountType: e.target.value as 'alpha_guest' | 'alpha_member',
                }))
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="alpha_guest">Guest</option>
              <option value="alpha_member">Member</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Expiration (days)</label>
            <Input
              type="number"
              value={newLinkData.expirationDays}
              onChange={(e) =>
                setNewLinkData((prev) => ({
                  ...prev,
                  expirationDays: parseInt(e.target.value) || 7,
                }))
              }
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button color="zinc" onClick={() => setIsGenerateDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleGenerateLink}>Generate Link</Button>
        </div>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)}>
        <DialogTitle>Edit Signup Link</DialogTitle>
        <DialogDescription>View and manage signup link details.</DialogDescription>

        <div className="mt-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Signup URL</label>
            <Input value={selectedLink?.url || ''} readOnly />
          </div>

          <div className="space-y-4">
            <h3 className="font-medium">Email Messages</h3>
            {selectedLink?.emails?.map((email, index) => (
              <div key={index} className="border rounded-md">
                <button
                  className="w-full flex items-center justify-between p-4"
                  onClick={() => toggleEmailExpand(`${selectedLink.signUpCode}-${index}`)}
                >
                  <span className="text-sm">
                    Email {index + 1} - {new Date(email.sentDate || Date.now()).toLocaleString()}
                  </span>
                  {expandedEmails[`${selectedLink.signUpCode}-${index}`] ? (
                    <ChevronDownIcon className="h-5 w-5" />
                  ) : (
                    <ChevronRightIcon className="h-5 w-5" />
                  )}
                </button>
                {expandedEmails[`${selectedLink.signUpCode}-${index}`] && (
                  <div className="px-4 pb-4">
                    <pre className="whitespace-pre-wrap text-sm">
                      {typeof email.message === 'object' ? (
                        <>
                          <strong>Subject:</strong> {email.message.subject}
                          <br />
                          <strong>Content:</strong> {email.message.text}
                        </>
                      ) : (
                        email.message
                      )}
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button color="zinc" onClick={() => setIsEditDialogOpen(false)}>
            Close
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
